import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import Button from "../common/Button";
import Input from "../common/Input";
import { changePassword, refreshAuthToken } from "../../services/accountService";

/**
 * Change password — ported from mobile's change-password screen
 * (src/Screens/change-password). After a successful change, mobile rotates
 * the session token via Account/RefreshToken rather than forcing a re-login;
 * matched here using the TokenInfo already nested on the stored user object
 * from login (see the approved plan's Auth note).
 */
const PasswordTab = () => {
  const { t } = useTranslation(["settings", "common"]);
  const { user, updateToken } = useAuth();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleSave = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error(t("passwordTab.toasts.allFieldsRequired"));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("passwordTab.toasts.passwordsDontMatch"));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t("passwordTab.toasts.passwordTooShort"));
      return;
    }

    setSaving(true);
    try {
      const res = await changePassword({
        userId: user?.UserId,
        oldPassword,
        newPassword,
        confirmPassword,
      });
      toast.success(res.data?.Message || t("passwordTab.toasts.changedSuccess"));
      reset();

      const tokenInfo = user?.TokenInfo;
      if (tokenInfo?.Token) {
        try {
          const refreshRes = await refreshAuthToken({
            Token: tokenInfo.Token,
            RefreshToken: tokenInfo.RefreshToken,
            ExpiryDate: tokenInfo.ExpiryDate,
            Issuer: tokenInfo.Issuer,
          });
          const newToken = refreshRes.data?.Result?.Token;
          if (newToken) updateToken(newToken);
        } catch {
          toast.info(t("passwordTab.toasts.sessionRefreshFailed"));
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.Message || t("passwordTab.toasts.changeFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.wrap}>
      <Input label={t("passwordTab.currentPasswordLabel")} type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} placeholder="•••••••••" />
      <Input label={t("passwordTab.newPasswordLabel")} type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="•••••••••" />
      <Input label={t("passwordTab.confirmNewPasswordLabel")} type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="•••••••••" />
      <Button onClick={handleSave} disabled={saving} style={{ marginTop: 8 }}>
        {saving ? t("passwordTab.changing") : t("passwordTab.changeButton")}
      </Button>
    </div>
  );
};

const s = {
  wrap: { maxWidth: 380 },
};

export default PasswordTab;
